import { useCallback, useContext, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { useHistory } from "react-router-dom";
import {
  Modal,
  Box,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import { useSnackbar } from "notistack";
import CloseIcon from "@mui/icons-material/Close";
import Header from "../../components/Header";
import useUser from "../../hooks/useUser";
import { api } from "../../services/Api";
import { formatarDataParaBR } from "../../utils/formatarUTCString";
import { Context } from "../../Context/PermissoesContext";
import SelecionarVisualizacao from "./SelecionarVisualizacao";
import ConteudoMapa from "./ConteudoMapa";
import MapaTraceAbas from "./MapaTraceAbas";
import ModalUnidade from "../MapaCurvas/ModalUnidade";
import "./styles.css";

function mapSituacaoParaStatus(valorSituacao) {
  return valorSituacao === "concluido" ? "removido" : "ativo";
}

export default function MapaTrace() {
  const { enqueueSnackbar } = useSnackbar();
  const usuario = useUser();
  const history = useHistory();
  const { checarPermissaoSemLog } = useContext(Context);

  const [aba, setAba] = useState("amostras");
  const [situacao, setSituacao] = useState("pendente");
  const [dataFim, setDataFim] = useState(() => new Date());
  const [unidade, setUnidade] = useState(0);
  const [placeholderUnidadeButton, setPlaceholderUnidadeButton] = useState("Unidade");
  const [isModalUnidadeOpen, setIsModalUnidadeOpen] = useState(false);
  const [modalAdicionarAmostraOpen, setModalAdicionarAmostraOpen] = useState(false);
  const [biomeriuexInput, setBiomeriuexInput] = useState("");
  const [consultaTubo, setConsultaTubo] = useState(null);
  const [mnemonicoEscolhido, setMnemonicoEscolhido] = useState("");
  const [minutosIncubacaoInput, setMinutosIncubacaoInput] = useState("");
  const [cards, setCards] = useState([]);
  const [carregandoCards, setCarregandoCards] = useState(false);
  const [processandoConsulta, setProcessandoConsulta] = useState(false);
  const [processandoAdicionar, setProcessandoAdicionar] = useState(false);
  const inputBiomeriuexRef = useRef(null);

  function getUnidadeSelecionada() {
    const valor = localStorage.getItem("@DNACenter:UnidadeDNA");
    const numero = Number(valor);
    return Number.isFinite(numero) && numero > 0 ? numero : 0;
  }

  const carregarCards = useCallback(
    async (idUnidade, valorSituacao) => {
      const unidadeNumero = Number(idUnidade);
      if (!Number.isFinite(unidadeNumero) || unidadeNumero <= 0) {
        setCards([]);
        return;
      }

      setCarregandoCards(true);
      try {
        const status = mapSituacaoParaStatus(valorSituacao);
        const resposta = await api.get(
          `/v2/mapaTrace/cards?id_unidade=${unidadeNumero}&status=${status}&data_fim=${dayjs(dataFim).valueOf()}`,
        );
        const lista = resposta.data?.data;
        setCards(Array.isArray(lista) ? lista : []);
      } catch (e) {
        const mensagem = (e.response?.data?.messages ?? "").trim();
        if (mensagem) {
          enqueueSnackbar(mensagem, {
            variant: "error",
            autoHideDuration: 3000,
          });
        }
        setCards([]);
      } finally {
        setCarregandoCards(false);
      }
    },
    [dataFim, enqueueSnackbar],
  );

  useEffect(() => {
    const nomeUnidade = localStorage.getItem("@DNACenter:NomeUnidadeDNA");
    const rotuloUnidade =
      nomeUnidade !== null ? nomeUnidade : "Selecione a Unidade";

    if (!localStorage.getItem("@DNACenter:UnidadeDNA")) {
      setUnidade(0);
      setIsModalUnidadeOpen(true);
      setPlaceholderUnidadeButton(rotuloUnidade);
      return;
    }
    setUnidade(getUnidadeSelecionada());
    setPlaceholderUnidadeButton(rotuloUnidade);
  }, []);

  useEffect(() => {
    if (!unidade || aba !== "amostras") return;
    carregarCards(unidade, situacao);
  }, [unidade, situacao, carregarCards, aba]);

  const podeConfigurarIncubacao = checarPermissaoSemLog("MTR002");

  useEffect(() => {
    if (!podeConfigurarIncubacao && aba === "configuracao") {
      setAba("amostras");
    }
  }, [podeConfigurarIncubacao, aba]);

  function handleCloseModalAdicionarAmostra() {
    setModalAdicionarAmostraOpen(false);
    setBiomeriuexInput("");
    setConsultaTubo(null);
    setMnemonicoEscolhido("");
    setMinutosIncubacaoInput("");
  }

  function preencherDadosConsultaTubo(data) {
    const mnemonico = String(data?.mnemonico ?? "").trim().toUpperCase();
    const minutosIncubacao = data?.minutos_incubacao;

    setConsultaTubo(data ?? null);
    setMnemonicoEscolhido(mnemonico);
    setMinutosIncubacaoInput(minutosIncubacao == null ? "" : String(minutosIncubacao));
  }

  async function consultarTubo() {
    if (processandoConsulta || !biomeriuexInput?.trim()) return;

    setProcessandoConsulta(true);
    try {
      const biomeriuex = biomeriuexInput.trim().toUpperCase();
      const resposta = await api.get(
        `/v2/mapaTrace/tubos/consulta/${encodeURIComponent(biomeriuex)}`,
      );
      const data = resposta.data?.data ?? null;
      preencherDadosConsultaTubo(data);
    } catch (e) {
      setConsultaTubo(null);
      setMnemonicoEscolhido("");
      setMinutosIncubacaoInput("");
      const mensagem = (e.response?.data?.messages ?? "").trim();
      if (mensagem) {
        const variant = e.response?.status === 404 ? "warning" : "error";
        enqueueSnackbar(mensagem, { variant, autoHideDuration: 3000 });
      }
    } finally {
      setProcessandoConsulta(false);
    }
  }

  function obterDadosAdicionarAmostra() {
    const biomeriuex = String(consultaTubo?.biomeriuex ?? "").trim();

    if (!biomeriuex) {
      return null;
    }

    const mnemonicoNormalizado = mnemonicoEscolhido.trim().toUpperCase();

    if (!mnemonicoNormalizado) {
      enqueueSnackbar("Informe o mnemonico.", {
        variant: "warning",
        autoHideDuration: 3000,
      });
      return null;
    }

    const minutosTexto = String(minutosIncubacaoInput ?? "").trim();

    if (!minutosTexto) {
      enqueueSnackbar("Informe minutos de incubação.", {
        variant: "warning",
        autoHideDuration: 3000,
      });
      return null;
    }

    const minutosIncubacao = Number(minutosTexto);

    if (!Number.isInteger(minutosIncubacao)) {
      enqueueSnackbar("Informe minutos de incubação como número inteiro válido.", {
        variant: "warning",
        autoHideDuration: 3000,
      });
      return null;
    }

    return {
      biomeriuex,
      mnemonicoEscolhido: mnemonicoNormalizado,
      minutosIncubacao,
    };
  }

  async function handleAdicionarAmostra() {
    if (processandoAdicionar) return;

    const dadosAdicionar = obterDadosAdicionarAmostra();
    if (!dadosAdicionar) return;

    setProcessandoAdicionar(true);
    try {
      await api.post("/v2/mapaTrace/cards", {
        id_unidade: unidade,
        biomeriuex: dadosAdicionar.biomeriuex,
        mnemonico_escolhido: dadosAdicionar.mnemonicoEscolhido,
        minutos_incubacao: dadosAdicionar.minutosIncubacao,
      });

      enqueueSnackbar("Amostra adicionada com sucesso.", {
        variant: "success",
        autoHideDuration: 3000,
      });
      handleCloseModalAdicionarAmostra();
      await carregarCards(unidade, situacao);
    } catch (e) {
      const mensagem = (e.response?.data?.messages ?? "").trim();
      if (mensagem) {
        enqueueSnackbar(mensagem, { variant: "error", autoHideDuration: 3000 });
      }
    } finally {
      setProcessandoAdicionar(false);
    }
  }

  const confirmarRemocaoCard = useCallback(
    async (id) => {
      try {
        await api.post(`/v2/mapaTrace/cards/${id}/confirmar-remocao`);
        enqueueSnackbar("Remoção confirmada com sucesso.", {
          variant: "success",
          autoHideDuration: 3000,
        });
        await carregarCards(unidade, situacao);
      } catch (e) {
        const mensagem = (e.response?.data?.messages ?? "").trim();
        if (mensagem) {
          enqueueSnackbar(mensagem, {
            variant: "error",
            autoHideDuration: 3000,
          });
        }
        throw e;
      }
    },
    [carregarCards, enqueueSnackbar, situacao, unidade],
  );

  useEffect(() => {
    if (!modalAdicionarAmostraOpen) return;
    const timer = setTimeout(() => inputBiomeriuexRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, [modalAdicionarAmostraOpen]);

  function handleBiomeriuexKeyDown(e) {
    if (e.key !== "Enter" || !biomeriuexInput.trim()) return;
    e.preventDefault();
    consultarTubo();
  }

  function handleLimparConsulta() {
    setBiomeriuexInput("");
    setConsultaTubo(null);
    setMnemonicoEscolhido("");
    setMinutosIncubacaoInput("");
    setTimeout(() => inputBiomeriuexRef.current?.focus(), 100);
  }

  const mnemonicoDetectadoNoTubo =
    String(consultaTubo?.mnemonico ?? "").trim().length > 0;

  return (
    <>
      <Header titulo="Mapa Trace" usuario={usuario} history={history} />

      <div className="mapa-trace mapa-trace-shell">
        <MapaTraceAbas
          aba={aba}
          setAba={setAba}
          podeConfigurarIncubacao={podeConfigurarIncubacao}
        >
            <>
              <SelecionarVisualizacao
                setSituacao={setSituacao}
                situacao={situacao}
                dataFim={dataFim}
                setDataFim={setDataFim}
                onAdicionarAmostra={() => setModalAdicionarAmostraOpen(true)}
                nomeUnidade={placeholderUnidadeButton}
                onAbrirModalUnidade={() => setIsModalUnidadeOpen(true)}
              />

              <ModalUnidade
                isModalUnidadeOpen={isModalUnidadeOpen}
                handleCloseModalUnidade={() => setIsModalUnidadeOpen(false)}
                unidade={unidade}
                setUnidade={setUnidade}
                setPlaceholderUnidadeButton={setPlaceholderUnidadeButton}
              />

              <ConteudoMapa
                cards={cards}
                loading={carregandoCards}
                onConfirmarRemocao={confirmarRemocaoCard}
              />
            </>
        </MapaTraceAbas>

        <Modal
          open={modalAdicionarAmostraOpen}
          onClose={handleCloseModalAdicionarAmostra}
          aria-labelledby="modal-adicionar-amostra-title"
          aria-describedby="modal-adicionar-amostra-description"
        >
          <Box
            className={`modal-adicionar-amostra${
              consultaTubo ? " modal-adicionar-amostra-com-consulta" : ""
            }`}
          >
            <h2
              className="modal-adicionar-amostra-title"
              id="modal-adicionar-amostra-title"
            >
              Adicionar Amostra
              <IconButton
                aria-label="Fechar"
                className="modal-adicionar-amostra-fechar"
                onClick={handleCloseModalAdicionarAmostra}
              >
                <CloseIcon />
              </IconButton>
            </h2>
            <p
              id="modal-adicionar-amostra-description"
              className="modal-adicionar-amostra-desc"
            >
              Escaneie o código da amostra no leitor.
            </p>
            <TextField
              inputRef={inputBiomeriuexRef}
              label="Código da amostra"
              value={biomeriuexInput}
              onChange={(e) => {
                setBiomeriuexInput(e.target.value);
                if (!consultaTubo) return;
                setConsultaTubo(null);
                setMnemonicoEscolhido("");
                setMinutosIncubacaoInput("");
              }}
              onKeyDown={handleBiomeriuexKeyDown}
              variant="outlined"
              size="small"
              fullWidth
              autoComplete="off"
              className="modal-adicionar-amostra-input"
              inputProps={{
                "aria-label": "Código da amostra para escanear",
              }}
            />
            <div className="modal-adicionar-amostra-acoes modal-adicionar-amostra-acoes--apos-input">
              <Button
                variant="outlined"
                size="small"
                onClick={consultarTubo}
                disabled={!biomeriuexInput.trim() || processandoConsulta}
              >
                {processandoConsulta ? "Consultando..." : "Consultar"}
              </Button>
            </div>

            {consultaTubo && (
              <div className="modal-adicionar-amostra-info-tubo">
                <h3 className="modal-adicionar-amostra-info-titulo">
                  Confirme os dados da amostra
                </h3>
                <dl className="modal-adicionar-amostra-lista-info">
                  <dt>Paciente:</dt>
                  <dd>{consultaTubo.paciente?.nome || "—"}</dd>
                  <dt>Data de nascimento:</dt>
                  <dd>
                    {consultaTubo.paciente?.dataNascimento
                      ? formatarDataParaBR(
                          new Date(consultaTubo.paciente.dataNascimento),
                        ).split(" ")[0]
                      : "—"}
                  </dd>
                  <dt>Código da amostra:</dt>
                  <dd>{consultaTubo.biomeriuex}</dd>
                  <dt>Código do Tubo:</dt>
                  <dd>{consultaTubo.codigo_tubo || "—"}</dd>
                  <dt>Mnemonicos:</dt>
                  <dd>{consultaTubo.mneumonicos?.trim() || "—"}</dd>
                  <dt>Material:</dt>
                  <dd>{consultaTubo.material || "—"}</dd>
                </dl>

                <TextField
                  label="Mnemonico"
                  value={mnemonicoEscolhido}
                  onChange={(event) =>
                    setMnemonicoEscolhido(event.target.value.toUpperCase())
                  }
                  disabled={processandoAdicionar || mnemonicoDetectadoNoTubo}
                  margin="dense"
                  fullWidth
                  helperText={
                    mnemonicoDetectadoNoTubo
                      ? "Mnemonico definido automaticamente a partir do tubo."
                      : ""
                  }
                />

                <TextField
                  label="Minutos de incubação"
                  type="number"
                  value={minutosIncubacaoInput}
                  onChange={(event) =>
                    setMinutosIncubacaoInput(event.target.value)
                  }
                  disabled={processandoAdicionar}
                  margin="dense"
                  fullWidth
                  inputProps={{
                    step: 1,
                  }}
                  helperText="Informe um número inteiro."
                />

                <div className="modal-adicionar-amostra-acoes">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleLimparConsulta}
                  >
                    Escanear outro
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAdicionarAmostra}
                    disabled={processandoAdicionar}
                  >
                    {processandoAdicionar ? "Adicionando..." : "Adicionar"}
                  </Button>
                </div>
              </div>
            )}
          </Box>
        </Modal>
      </div>
    </>
  );
}
